import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, message, Modal, theme, Typography } from "antd";
import { ArrowRightOutlined, CloseOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  IMerchantDetails,
  IPartnerDeal,
  OnlinePartnerBucketGroups,
  PartnerBucketGroupPrice,
  PartnerBucketGroups,
} from "../../../../interfaces/partnerDeal.interface";
import {
  BucketGroupFieldNames,
  IOnlinePurchPriceProfile,
  IPurchPriceProfile,
  PartnerBucketGroupMinMaxPrice,
} from "../../../../interfaces/partner.interface";
import {
  OnlineGroupKey,
  OnlinePricingStructure,
} from "../../../../interfaces/onlinePricing.interface";
import {
  filterAllowedOnlineProfiles,
  resolveOnlineBand,
  selectOnlineProfile,
} from "./dealOnlinePricing";
import {
  OnlineBucketKey,
  detectOnlinePartnerPricingStructure,
  getOnlinePartnerGroupKeys,
  onlinePricingTemplateForCountry,
} from "../../../partners/constants/onlineBuckets";
import { pricedOrUndefined } from "../../../partners/utils/pricedOrUndefined";
import {
  onlineGroupLabel,
  onlineGroupValueType,
} from "../../../businesses/utils/sixteenBucketOnlinePricing";
import { DEFAULT_AMEX_BUCKET } from "../../../common/constants/defaultAmexBucket";
import { reconcileAmexBucketGroupForSave } from "../../../common/constants/inPersonAmexVisibility";
import { selectIsKlearlyAdmin } from "../../../access-control/accountSlice";
import { selectHideInPersonAmex } from "../../../feature-flags/featureFlagsSlice";
import {
  getPartnerThunk,
  selectCurrentPartner,
} from "../../../partners/partnersSlice";
import {
  createPartnerDealsThunk,
  selectCurrentPartnerDeal,
} from "../../partnerDealsSlice";
import { validatePartnerDealRegistrationNumberServer } from "../../partnerDealsApi";
import { DEFAULT_PURCH_PRICE_PROFILE } from "../../utils/defaultPurchPriceProfile";
import WizardStepper from "../../../common/wizard/WizardStepper";
import { DEAL_WIZARD_STEPS, WizardMode } from "./dealWizardSteps";
import MerchantDetailsStep from "./steps/MerchantDetailsStep";
import PricingBucketsStep, {
  PricingBucketRow,
} from "./steps/PricingBucketsStep";
import { describeOutOfRange, isWithinRange } from "./dealPricingRange";

const { Title, Text } = Typography;

interface DealWizardProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  mode: WizardMode;
}

const LAST_STEP = DEAL_WIZARD_STEPS.length - 1;

const IN_PERSON_BUCKETS: BucketGroupFieldNames[] = [
  "domesticDebitCards",
  "europeanDebitCards",
  "domesticCreditCards",
  "european",
  "internationalAndCommercial",
  "amex",
];

const IN_PERSON_LABELS: Record<BucketGroupFieldNames, string> = {
  domesticDebitCards: "Domestic Debit",
  domesticCreditCards: "Domestic Credit",
  europeanDebitCards: "European Debit",
  european: "E.E.A",
  internationalAndCommercial: "Non E.E.A & Corporate",
  amex: "AMEX",
};

// Seeded bands for the four-bucket shape only. The 16-bucket groups get none on
// purpose: a partner priced on them always carries every group its split needs,
// so an absent band is bad partner data, not something to paper over with an
// invented rate (see the zero-band guard in commitOnlinePricing).
// bancontact's floor/value/max below are unconfirmed — copied verbatim from iDeal, not a priced Bancontact rate; needs product/KBM input.
const DEFAULT_ONLINE_FLOORS: Record<OnlineBucketKey, PartnerBucketGroupPrice> =
  {
    iDeal: { value: 0.18, valueType: "Fixed" },
    bancontact: { value: 0.18, valueType: "Fixed" },
    amex: { value: 2.5, valueType: "Percentage" },
    corporateCreditCards: { value: 1.8, valueType: "Percentage" },
    nonCorporateCreditCards: { value: 1.2, valueType: "Percentage" },
    appleGooglePay: { value: 1.8, valueType: "Percentage" },
  };

const DEFAULT_ONLINE_VALUES: Record<OnlineBucketKey, PartnerBucketGroupPrice> =
  {
    iDeal: { value: 0.25, valueType: "Fixed" },
    bancontact: { value: 0.25, valueType: "Fixed" },
    amex: { value: 2.95, valueType: "Percentage" },
    corporateCreditCards: { value: 2.2, valueType: "Percentage" },
    nonCorporateCreditCards: { value: 1.55, valueType: "Percentage" },
    appleGooglePay: { value: 2.2, valueType: "Percentage" },
  };

const DEFAULT_ONLINE_MAX: Record<OnlineBucketKey, number> = {
  iDeal: 0.5,
  bancontact: 0.5,
  amex: 3.8,
  corporateCreditCards: 3.2,
  nonCorporateCreditCards: 2.5,
  appleGooglePay: 3.2,
};

// PC-757: in-person AMEX is a standard bucket on every price range. Resolve the
// floor to the stored bucket only when it's a valid Percentage; a missing or
// legacy Fixed AMEX bucket falls back to the canonical 3.3% / 4% default so the
// row always renders as "%" and never shows "€" or a false "below floor".
const resolveAmexFloor = (
  stored?: PartnerBucketGroupMinMaxPrice
): PartnerBucketGroupMinMaxPrice =>
  stored && stored.valueType === "Percentage" ? stored : DEFAULT_AMEX_BUCKET;

export default function DealWizard({
  visible,
  setVisible,
  mode,
}: DealWizardProps) {
  const { token } = theme.useToken();
  const dispatch = useAppDispatch();
  const isKlearlyAdmin = useAppSelector(selectIsKlearlyAdmin);
  const hideInPersonAmex = useAppSelector(selectHideInPersonAmex);
  const visibleInPersonBuckets = useMemo(
    () =>
      hideInPersonAmex
        ? IN_PERSON_BUCKETS.filter((key) => key !== "amex")
        : IN_PERSON_BUCKETS,
    [hideInPersonAmex]
  );
  const currentPartner = useAppSelector(selectCurrentPartner);
  const currentPartnerDeal = useAppSelector(selectCurrentPartnerDeal);
  const [merchantForm] = Form.useForm();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Partial<IPartnerDeal>>({});
  const [selectedPartnerId, setSelectedPartnerId] = useState<
    string | undefined
  >();
  const [selectedProfileId, setSelectedProfileId] = useState<
    string | undefined
  >();
  const [selectedOnlineProfileId, setSelectedOnlineProfileId] = useState<
    string | undefined
  >();
  const [inPersonPricing, setInPersonPricing] = useState<
    Partial<PartnerBucketGroups>
  >({});
  const [onlinePricing, setOnlinePricing] = useState<
    Partial<OnlinePartnerBucketGroups>
  >({});

  const watchedName = Form.useWatch("businessName", merchantForm);
  const watchedKvk = Form.useWatch("businessRegistrationNumber", merchantForm);
  const dealInContext: Partial<IPartnerDeal> =
    mode === "edit" ? currentPartnerDeal : draft;

  useEffect(() => {
    if (!visible) return;

    setStep(0);
    setDraft({});
    merchantForm.resetFields();
    // Admins pick a partner per deal — don't pre-seed the last-used one lingering
    // in Redux across reopens. Partner users have a fixed partner, so keep it.
    setSelectedPartnerId(
      mode === "edit"
        ? currentPartnerDeal?.partnerId
        : isKlearlyAdmin
          ? undefined
          : currentPartner?._id
    );
  }, [visible]);

  const headerName =
    (mode === "create" ? watchedName : undefined) ||
    dealInContext?.businessName ||
    (mode === "create" ? "New merchant deal" : "Merchant deal");
  const headerKvk =
    (mode === "create" ? watchedKvk : undefined) ||
    dealInContext?.businessRegistrationNumber;
  const isLastStep = step === LAST_STEP;
  const partnerName =
    currentPartner?.name || dealInContext?.partnerName || "Partner";

  const allowedPurchProfiles = useMemo(() => {
    const profiles = currentPartner?.purchPriceProfiles?.filter((profile) => {
      if (!profile?.whitelist || profile.whitelist.length === 0) return true;
      return profile.whitelist.includes(
        dealInContext?.businessRegistrationNumber || ""
      );
    });
    return profiles?.length ? profiles : [DEFAULT_PURCH_PRICE_PROFILE];
  }, [
    currentPartner?.purchPriceProfiles,
    dealInContext?.businessRegistrationNumber,
  ]);

  const selectedProfile =
    allowedPurchProfiles.find((profile) => profile._id === selectedProfileId) ||
    allowedPurchProfiles.find((profile) => profile.name === "default") ||
    allowedPurchProfiles[0];

  // Online ranges are independent of in-person: the deal's online step selects
  // from the partner's onlinePurchPriceProfiles, not the in-person profile.
  const allowedOnlineProfiles = useMemo(
    () =>
      filterAllowedOnlineProfiles(
        currentPartner?.onlinePurchPriceProfiles,
        dealInContext?.businessRegistrationNumber
      ),
    [
      currentPartner?.onlinePurchPriceProfiles,
      dealInContext?.businessRegistrationNumber,
    ]
  );

  const selectedOnlineProfile = selectOnlineProfile(
    allowedOnlineProfiles,
    selectedOnlineProfileId
  );

  // Same precedence as the partner wizard's buildOnlineState: the selected
  // profile's own stored template wins, then the partner's own default, and
  // only when neither exists is it derived from country. Deriving from country
  // alone (as this used to) offers a Bancontact row a legacy BE partner has no
  // rate for — resolveOnlineBucket then falls back to invented placeholder
  // values, so the deal gets created against numbers nobody actually set.
  const onlineTemplate =
    selectedOnlineProfile?.pricingTemplate ??
    currentPartner?.defaultOnlinePricingTemplate ??
    onlinePricingTemplateForCountry(currentPartner?.country);

  // Which shape the deal is priced in follows the partner it is for, read off
  // its stored groups exactly as the backend reads them. A legacy partner keeps
  // the four-bucket shape.
  const onlineStructure = useMemo(
    () =>
      detectOnlinePartnerPricingStructure(
        selectedOnlineProfile?.bucketGroups,
        selectedOnlineProfile?.defaultOnlinePricing,
        currentPartner?.defaultOnlinePricing
      ) ?? OnlinePricingStructure.FourBucket,
    [selectedOnlineProfile, currentPartner?.defaultOnlinePricing]
  );
  const isSixteenBucketOnline =
    onlineStructure === OnlinePricingStructure.SixteenBucket;

  const ONLINE_BUCKETS = useMemo(
    () =>
      getOnlinePartnerGroupKeys({
        structure: onlineStructure,
        template: onlineTemplate,
        coverage: selectedOnlineProfile?.onlineBucketGroupCoverage,
        stored:
          selectedOnlineProfile?.bucketGroups ??
          currentPartner?.defaultOnlinePricing,
      }),
    [
      onlineStructure,
      onlineTemplate,
      selectedOnlineProfile?.onlineBucketGroupCoverage,
      selectedOnlineProfile?.bucketGroups,
      currentPartner?.defaultOnlinePricing,
    ]
  );

  const close = () => {
    merchantForm.resetFields();
    setDraft({});
    setStep(0);
    setLoading(false);
    setVisible(false);
  };

  const handlePartnerSelect = async (partnerId: string) => {
    setSelectedPartnerId(partnerId);
    await dispatch(getPartnerThunk(partnerId));
  };

  const buildInPersonPricing = (
    profile?: IPurchPriceProfile
  ): Partial<PartnerBucketGroups> => {
    const next: Partial<PartnerBucketGroups> = {};

    visibleInPersonBuckets.forEach((key) => {
      const stored = profile?.bucketGroups?.[key];
      const floor = key === "amex" ? resolveAmexFloor(stored) : stored;
      if (!floor) return;
      const valueType = key === "amex" ? "Percentage" : floor.valueType;
      // Per range, falling back to the partner-level section when unpriced.
      const partnerDefaultRaw =
        pricedOrUndefined(profile?.defaultPricing?.[key]) ??
        currentPartner?.defaultPricing?.[key];
      // AMEX is always Percentage: ignore a legacy Fixed partner default and
      // fall back to the canonical floor (3.3%) so the seeded value is valid.
      const partnerDefault =
        key === "amex"
          ? partnerDefaultRaw?.valueType === "Percentage"
            ? partnerDefaultRaw
            : floor
          : partnerDefaultRaw || floor;
      next[key] = {
        value: partnerDefault.value,
        valueType,
      };
    });

    return next;
  };

  // A 16-bucket group has no seeded band to fall back to, so it resolves to a
  // zero one and the create is blocked rather than priced at a number nobody set.
  const onlineFallbackValue = (key: OnlineGroupKey): PartnerBucketGroupPrice =>
    DEFAULT_ONLINE_VALUES[key as OnlineBucketKey] ?? {
      value: 0,
      valueType: onlineGroupValueType(key) ?? "Fixed",
    };

  const resolveOnlineBucket = (
    key: OnlineGroupKey,
    profile?: IOnlinePurchPriceProfile
  ) =>
    resolveOnlineBand(
      profile?.bucketGroups?.[key],
      currentPartner?.defaultOnlinePricing?.[key] || onlineFallbackValue(key),
      DEFAULT_ONLINE_FLOORS[key as OnlineBucketKey]?.value ?? 0,
      DEFAULT_ONLINE_MAX[key as OnlineBucketKey] ?? 0
    );

  const buildOnlinePricing = (
    profile?: IOnlinePurchPriceProfile
  ): Partial<OnlinePartnerBucketGroups> =>
    ONLINE_BUCKETS.reduce<Partial<OnlinePartnerBucketGroups>>((acc, key) => {
      acc[key] = resolveOnlineBucket(key, profile);
      return acc;
    }, {});

  useEffect(() => {
    if (!visible) return;
    const profile =
      allowedPurchProfiles.find((p) => p.name === "default") ||
      allowedPurchProfiles[0];
    setSelectedProfileId(profile?._id);
    setInPersonPricing(
      mode === "edit" && currentPartnerDeal?.bucketGroups
        ? currentPartnerDeal.bucketGroups
        : buildInPersonPricing(profile)
    );
    // In edit mode, round-trip the deal's persisted online range so reopening it
    // shows the range it was created on, not the partner default.
    const onlineProfile =
      (mode === "edit"
        ? allowedOnlineProfiles.find(
            (p) => p._id === currentPartnerDeal?.onlinePurchPriceProfileId
          )
        : undefined) ||
      allowedOnlineProfiles.find((p) => p.name === "default") ||
      allowedOnlineProfiles[0];
    setSelectedOnlineProfileId(onlineProfile?._id);
    setOnlinePricing(
      mode === "edit" && currentPartnerDeal?.onlineBucketGroups
        ? currentPartnerDeal.onlineBucketGroups
        : buildOnlinePricing(onlineProfile)
    );
  }, [
    allowedPurchProfiles,
    allowedOnlineProfiles,
    currentPartner?._id,
    currentPartnerDeal,
    mode,
    visible,
  ]);

  const commitMerchantDetails = async (): Promise<boolean> => {
    if (mode === "edit") return true;

    let values: IMerchantDetails;
    try {
      values = (await merchantForm.validateFields()) as IMerchantDetails;
    } catch {
      return false;
    }

    if (isKlearlyAdmin && !selectedPartnerId) {
      message.error("Please pick the partner this deal belongs to.");
      return false;
    }

    setLoading(true);
    try {
      // KVK uniqueness is global, so validation intentionally omits partnerId.
      await validatePartnerDealRegistrationNumberServer(
        values.businessRegistrationNumber
      );
      merchantForm.setFields([
        { name: "businessRegistrationNumber", errors: [] },
      ]);
    } catch (error) {
      merchantForm.setFields([
        {
          name: "businessRegistrationNumber",
          errors: [(error as Error).message],
        },
      ]);
      return false;
    } finally {
      setLoading(false);
    }

    setDraft((prev) => ({
      ...prev,
      ...values,
      partnerId: selectedPartnerId || currentPartner?._id,
      partnerName: currentPartner?.name,
    }));
    return true;
  };

  const inPersonRows = useMemo<PricingBucketRow[]>(() => {
    return visibleInPersonBuckets.flatMap((key) => {
      const stored = selectedProfile?.bucketGroups?.[key];
      const floor = key === "amex" ? resolveAmexFloor(stored) : stored;
      if (!floor) return [];
      const valueType = key === "amex" ? "Percentage" : floor.valueType;
      const value = inPersonPricing[key] || {
        value: floor.value,
        valueType,
      };
      return [
        {
          key,
          label: IN_PERSON_LABELS[key],
          floor: { value: floor.value, valueType },
          value: { value: value.value, valueType },
          max: floor.max,
        },
      ];
    });
  }, [inPersonPricing, selectedProfile, visibleInPersonBuckets]);

  const onlineRows = useMemo<PricingBucketRow[]>(() => {
    return ONLINE_BUCKETS.map((key) => {
      // Floor/max follow the selected online range; value stays the edited /
      // partner-default online rate.
      const band = resolveOnlineBucket(key, selectedOnlineProfile);
      const value = onlinePricing[key] || band;

      return {
        key,
        label: onlineGroupLabel(key),
        floor: { value: band.min, valueType: band.valueType },
        value: { value: value.value, valueType: band.valueType },
        max: band.max,
      };
    });
  }, [
    ONLINE_BUCKETS,
    currentPartner?.defaultOnlinePricing,
    onlinePricing,
    selectedOnlineProfile,
  ]);

  const commitInPersonPricing = (): boolean => {
    if (mode === "edit") return true;
    if (
      !selectedProfile?._id &&
      selectedProfile !== DEFAULT_PURCH_PRICE_PROFILE
    ) {
      message.error("Please select a price range.");
      return false;
    }
    if (inPersonRows.length === 0) {
      message.error("No in-person pricing buckets found for this partner.");
      return false;
    }
    const outOfRange = inPersonRows.filter((row) => !isWithinRange(row));
    if (outOfRange.length > 0) {
      message.error(
        `Keep in-person prices within floor and max: ${describeOutOfRange(outOfRange)}.`
      );
      return false;
    }

    setDraft((prev) => ({
      ...prev,
      purchPriceProfileId: selectedProfile?._id || "",
      // create-only path (edit returns above); never invent Amex from defaults.
      bucketGroups: reconcileAmexBucketGroupForSave(
        inPersonPricing as PartnerBucketGroups,
        undefined,
        hideInPersonAmex
      ) as PartnerBucketGroups,
    }));
    return true;
  };

  // Mirror partnerPricing's degenerate-band guard: an online bucket left at 0
  // means "not offered" — never persist a {0} band that would block the
  // merchant. Drop unconfigured buckets and omit online entirely if all empty.
  //
  // A 16-bucket deal is the exception: it must carry every group its card split
  // and local method price, or the save rejects it as incomplete. Nothing is
  // dropped from one, and commitOnlinePricing has already refused to build one
  // that would carry a zero.
  const buildOnlinePayload = (): Pick<
    IPartnerDeal,
    "onlineBucketGroups" | "onlinePricingTemplate" | "onlinePurchPriceProfileId"
  > => {
    const groups: Partial<OnlinePartnerBucketGroups> = {};
    if (isSixteenBucketOnline) {
      // Off the rows, not the edit state: a group the card split only started
      // pricing when the in-person range changed has no entry in the state yet,
      // and leaving it out is the incompleteness the save refuses.
      if (!onlineRows.some((row) => row.value.value > 0)) return {};
      onlineRows.forEach((row) => {
        groups[row.key as OnlineGroupKey] = row.value;
      });
    } else {
      ONLINE_BUCKETS.forEach((key) => {
        const bucket = onlinePricing[key];
        if (!bucket || bucket.value <= 0) return;
        groups[key] = bucket;
      });
      if (Object.keys(groups).length === 0) return {};
    }
    return {
      onlineBucketGroups: groups,
      onlinePricingTemplate: onlineTemplate,
      // Only when the partner actually has an online range; the backend validates
      // the online buckets against it and skips validation when it's absent.
      ...(selectedOnlineProfile?._id
        ? { onlinePurchPriceProfileId: selectedOnlineProfile._id }
        : {}),
    };
  };

  // A create sends every online value, seeded ones included, so an untouched
  // out-of-band row would persist a bad rate — this stays a hard block.
  const commitOnlinePricing = (): boolean => {
    if (mode === "edit") return true;
    // A 16-bucket deal has to carry every group, so one with no rate cannot be
    // dropped the way a four-bucket bucket can — and sending its zero would
    // price the merchant at nothing. Stop, and name the partner data at fault.
    if (isSixteenBucketOnline) {
      const unpriced = onlineRows.filter((row) => row.value.value <= 0);
      if (unpriced.length > 0 && unpriced.length < onlineRows.length) {
        message.error(
          `This partner has no online rate for ${unpriced
            .map((row) => row.label)
            .join(
              ", "
            )}. Fix the partner's online pricing before creating the deal.`
        );
        return false;
      }
    }
    const outOfRange = onlineRows.filter(
      (row) => row.value.value > 0 && !isWithinRange(row)
    );
    if (outOfRange.length > 0) {
      message.error(
        `Deal not created — keep online prices within floor and max: ${describeOutOfRange(outOfRange)}.`
      );
      return false;
    }
    return true;
  };

  const createDeal = async () => {
    if (!commitOnlinePricing()) return;

    const partnerDeal: Partial<IPartnerDeal> = {
      ...draft,
      partnerId: selectedPartnerId || currentPartner?._id,
      partnerName: draft.partnerName || currentPartner?.name,
      purchPriceProfileId: selectedProfile?._id || "",
      bucketGroups: reconcileAmexBucketGroupForSave(
        inPersonPricing as PartnerBucketGroups,
        undefined,
        hideInPersonAmex
      ) as PartnerBucketGroups,
      ...buildOnlinePayload(),
    };

    setLoading(true);
    const result = (await dispatch(createPartnerDealsThunk([partnerDeal]))) as {
      payload?: unknown;
      error?: { message?: string };
    };
    setLoading(false);

    if (result?.payload && !result?.error) {
      message.success("Merchant deal created.");
      close();
      return;
    }

    message.error(result?.error?.message || "Could not create merchant deal.");
  };

  const goToStep = async (target: number) => {
    if (target === step) return;
    if (target > step && step === 0) {
      const ok = await commitMerchantDetails();
      if (!ok) return;
    }
    if (target > step && step === 1 && !commitInPersonPricing()) return;
    setStep(target);
  };

  const handlePrimary = async () => {
    if (step === 0) {
      const ok = await commitMerchantDetails();
      if (!ok) return;
    }
    if (step === 1 && !commitInPersonPricing()) return;
    if (step === 2) {
      if (mode === "edit") close();
      else await createDeal();
      return;
    }
    if (!isLastStep) setStep((prev) => prev + 1);
  };

  const primaryLabel = useMemo(() => {
    if (!isLastStep) return "Save & continue";
    return mode === "create" ? "Create deal" : "Done";
  }, [isLastStep, mode]);

  const renderBody = () => {
    if (step === 0) {
      return (
        <MerchantDetailsStep
          form={merchantForm}
          mode={mode}
          details={dealInContext}
          showPartnerSelect={isKlearlyAdmin}
          selectedPartnerId={selectedPartnerId}
          onSelectPartner={handlePartnerSelect}
        />
      );
    }

    if (step === 1) {
      return (
        <PricingBucketsStep
          sectionTitle="Card buckets"
          sectionSubtitle="Same split logic as partner pricing — Klearly floor + partner markup = what this merchant is charged."
          rangeLabel="Price range"
          rangeHelp="Volume tier this merchant falls into. Drives the default markup we suggest."
          rangeOptions={allowedPurchProfiles.map((profile) => ({
            value: profile._id,
            label: profile.name || "Default",
          }))}
          selectedRangeId={selectedProfile?._id}
          rows={inPersonRows}
          onRangeChange={(profileId) => {
            const profile = allowedPurchProfiles.find(
              (item) => item._id === profileId
            );
            setSelectedProfileId(profileId);
            setInPersonPricing(buildInPersonPricing(profile));
          }}
          onValueChange={(key, value) => {
            const bucketKey = key as BucketGroupFieldNames;
            setInPersonPricing((prev) => ({
              ...prev,
              [bucketKey]: {
                value: Number(value ?? 0),
                valueType:
                  bucketKey === "amex"
                    ? "Percentage"
                    : selectedProfile?.bucketGroups?.[bucketKey]?.valueType ||
                      "Percentage",
              },
            }));
          }}
        />
      );
    }

    return (
      <PricingBucketsStep
        sectionTitle="Online buckets"
        sectionSubtitle="Payment links and e-commerce. Same split logic applies."
        rangeLabel="Price range"
        rangeHelp="Online volume tier this merchant falls into. Independent of in-person."
        rangeOptions={
          allowedOnlineProfiles.length
            ? allowedOnlineProfiles.map((profile) => ({
                value: profile._id,
                label: profile.name || "Default",
              }))
            : [{ value: "default", label: "Default" }]
        }
        selectedRangeId={selectedOnlineProfile?._id || "default"}
        rangeDisabled={allowedOnlineProfiles.length === 0}
        onRangeChange={(profileId) => {
          const profile = allowedOnlineProfiles.find(
            (item) => item._id === profileId
          );
          setSelectedOnlineProfileId(profileId);
          setOnlinePricing(buildOnlinePricing(profile));
        }}
        rows={onlineRows}
        onValueChange={(key, value) => {
          const onlineKey = key as OnlineGroupKey;
          const band = resolveOnlineBucket(onlineKey, selectedOnlineProfile);
          setOnlinePricing((prev) => ({
            ...prev,
            [onlineKey]: {
              value: Number(value ?? 0),
              valueType: band.valueType,
              min: band.min,
              max: band.max,
            },
          }));
        }}
      />
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={close}
      footer={null}
      width={1000}
      closeIcon={
        <CloseOutlined
          style={{ fontSize: 16, color: token.colorTextTertiary }}
        />
      }
      title={
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {step === 0
              ? mode === "create"
                ? "Create new merchant deal"
                : "Merchant deal"
              : DEAL_WIZARD_STEPS[step].title}
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {headerName}
            {step === 0 && headerKvk && <> · KVK {headerKvk}</>}
            {step > 0 && <> · {partnerName}</>}
          </Text>
        </div>
      }
    >
      <WizardStepper
        steps={DEAL_WIZARD_STEPS}
        current={step}
        reachableUpTo={step}
        onStepClick={goToStep}
        onBack={() => setStep((prev) => Math.max(0, prev - 1))}
        onForward={() => goToStep(Math.min(LAST_STEP, step + 1))}
      />

      <div
        style={{
          margin: "0 -24px",
          padding: "24px 24px 20px",
          background: token.colorBgLayout,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        {renderBody()}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          paddingTop: 16,
        }}
      >
        <Button
          onClick={close}
          style={{
            height: 40,
            padding: "0 24px",
            borderColor: token.colorPrimary,
            color: token.colorPrimary,
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          loading={loading}
          onClick={handlePrimary}
          icon={!isLastStep ? <ArrowRightOutlined /> : undefined}
          iconPosition="end"
        >
          {primaryLabel}
        </Button>
      </div>
    </Modal>
  );
}
